/**
 * Settings (cog) button for the image viewer's control bar.
 *
 * Mirrors the video player's ActionsMenu (js/player/components/ActionsMenu.tsx)
 * — same gear button, same shared Menu, same "Quality" radio section — but
 * without the playback-only items (speed, loop, playback settings) that make
 * ActionsMenu itself unusable here.
 *
 * Like ActionsMenu, this does not close itself when an item is picked: the
 * viewer closes it by dispatching CLOSE_SOURCE_MENU from its switch handler.
 */

import React, { useMemo, useRef } from 'react';
import { Menu, menuSection } from '../../components/Menu';
import type { MenuItem, MenuItemRadio } from '../../components/Menu';
import { PlayerButton } from '../../player/components/PlayerButton';
import type { ImageSource } from '../types';

export interface ImageActionsMenuProps {
  isOpen: boolean;
  /** Selectable renditions — proxies first, then the original. */
  sources: ImageSource[];
  activeSourceIndex: number;
  onToggle: () => void;
  onSourceChange: (index: number) => void;
}

export function ImageActionsMenu({
  isOpen,
  sources,
  activeSourceIndex,
  onToggle,
  onSourceChange,
}: ImageActionsMenuProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const items = useMemo<MenuItem[]>(() => {
    const sourceItems: MenuItemRadio[] = sources.map((source, idx) => ({
      type: 'radio' as const,
      id: `image-source-${idx}`,
      label: source.label,
      checked: activeSourceIndex === idx,
      onClick: () => onSourceChange(idx),
    }));
    return menuSection('Quality', sourceItems);
  }, [sources, activeSourceIndex, onSourceChange]);

  return (
    <div className="kollab-player-actions-menu-container">
      <PlayerButton
        buttonRef={triggerRef}
        className={`kollab-player-actions-btn ${isOpen ? 'kollab-player-actions-btn--active' : ''}`}
        tooltip="Settings"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={onToggle}
      />

      <Menu
        items={items}
        isOpen={isOpen}
        onClose={onToggle}
        ariaLabel="Settings"
        triggerRef={triggerRef}
        className="kollab-player-actions-dropdown"
      />
    </div>
  );
}

export default ImageActionsMenu;
